import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import PasswordInput from "./PasswordInput";
import { GoogleButton } from "./GoogleButton";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/feed");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const loggedIn = await login(email, password);
      if (loggedIn?.must_change_password) {
        navigate("/change-password");
      } else {
        navigate("/feed");
      }
    } catch (err) {
      toast({
        title: "Login failed",
        description: err?.response?.data?.detail || "Please check your details and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in to your Indosbook account."
      footer={
        <>
          New to Indosbook?{" "}
          <Link to="/signup" className="font-bold text-[#FF4500] hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <GoogleButton label="Log in with Google" />

      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-sm text-gray-400">or</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="h-12"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">PIN</Label>
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-[#FF4500] hover:underline"
            >
              Forgot PIN?
            </Link>
          </div>
          <PasswordInput
            id="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your 6-digit PIN"
            data-testid="login-pin"
          />
          <p className="text-xs text-gray-400">Joined before PINs? Your old password still works.</p>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="h-12 w-full rounded-full bg-[#FF4500] text-base font-bold text-white hover:bg-[#e03d00]"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log In"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;
